import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import Layout from '../components/layout/Layout';
import { useSiteContent } from '../context/SiteContentContext';
import { Search, Calendar, ArrowRight, Tag } from 'lucide-react';

const BlogPage = () => {
  const { siteContent } = useSiteContent();
  const blogPosts = siteContent.blogPosts;
  const blogCategories = siteContent.blogCategories;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');

  const categories = useMemo(() => {
    const fromConfig = Array.isArray(blogCategories) ? blogCategories.filter(Boolean) : [];
    if (fromConfig.length > 0) return ['Todos', ...fromConfig];

    const found = new Set();
    (blogPosts || []).forEach((p) => {
      if (p?.category) found.add(p.category);
    });
    return ['Todos', ...Array.from(found)];
  }, [blogPosts, blogCategories]);

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Todos' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  /*
  // Extended mock posts for better display
  const extendedPosts = [
    ...blogPosts,
    {
      id: 4,
      title: 'Outsourcing de TI: Vantagens e como escolher o parceiro ideal',
      excerpt: 'Entenda os benefícios de terceirizar sua TI e os critérios essenciais para escolher a empresa certa.',
      date: '2025-07-20',
      category: 'Consultoria',
      image: 'https://images.pexels.com/photos/845451/pexels-photo-845451.jpeg'
    },
    {
      id: 5,
      title: 'Redes corporativas: Boas práticas para alta performance',
      excerpt: 'Dicas essenciais para manter sua rede corporativa funcionando com máxima eficiência.',
      date: '2025-07-15',
      category: 'Infraestrutura',
      image: 'https://images.pexels.com/photos/442150/pexels-photo-442150.jpeg'
    },
    {
      id: 6,
      title: 'Backup em nuvem: Por que sua empresa não pode ficar sem',
      excerpt: 'A importância do backup em nuvem e como implementá-lo corretamente na sua empresa.',
      date: '2025-07-10',
      category: 'Cloud',
      image: 'https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg'
    }
  ];
  */

  const displayPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Todos' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute bottom-1/4 right-10 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm font-medium mb-6">
              Blog
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Notícias e <span className="text-orange-500">Artigos</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Fique por dentro das novidades do mundo da tecnologia e TI corporativa.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar artigos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 border-slate-200"
              />
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category
                      ? 'bg-orange-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {displayPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayPosts.map((post) => (
                <Card
                  key={post.id}
                  className="group border-0 shadow-lg shadow-slate-200/50 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-orange-100 text-orange-600 text-xs font-medium">
                        <Tag className="w-3 h-3" />
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.date).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-3 group-hover:text-orange-500 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <Link
                      to={`/blog/${post.id}`}
                      state={{ post }}
                      className="inline-flex items-center gap-2 text-orange-500 font-medium text-sm hover:gap-3 transition-all"
                    >
                      Ler mais
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">Nenhum artigo encontrado.</p>
              <Button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('Todos');
                }}
                variant="outline"
                className="mt-4"
              >
                Limpar filtros
              </Button>
            </div>
          )}

          {/* Pagination Placeholder */}
          {displayPosts.length > 0 && (
            <div className="flex justify-center mt-12">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled>
                  Anterior
                </Button>
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
                  1
                </Button>
                <Button variant="outline" size="sm">
                  2
                </Button>
                <Button variant="outline" size="sm">
                  3
                </Button>
                <Button variant="outline" size="sm">
                  Próximo
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
            Newsletter
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
            Receba nossas novidades
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Cadastre-se para receber conteúdos exclusivos sobre tecnologia e TI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Seu melhor e-mail"
              className="h-12 flex-1"
            />
            <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12 px-8">
              Cadastrar
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogPage;
